import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import PasswordInput from '../components/PasswordInput';
import '../styles/auth.css';

const PW_RULES = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'One uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: p => /[a-z]/.test(p) },
  { label: 'One number', test: p => /[0-9]/.test(p) },
  { label: 'One special character', test: p => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(p) },
];

export default function Signup() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [step, setStep] = useState('validating');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [require2fa, setRequire2fa] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setStep('invalid'); return; }
    fetch('/api/auth/validate-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    }).then(r => r.json()).then(data => {
      if (data.error) { setError(data.error); setStep('invalid'); }
      else { setEmail(data.email); setStep('send-code'); }
    }).catch(() => { setError('Connection error'); setStep('invalid'); });
  }, [token]);

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); setLoading(false); return; }
      setStep('verify-code');
      setLoading(false);
    } catch { setError('Connection error'); setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); setLoading(false); return; }
      setStep('set-password');
      setLoading(false);
    } catch { setError('Connection error'); setLoading(false); }
  };

  const completeSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) { setError('Passwords do not match'); return; }
    const failing = PW_RULES.filter(r => !r.test(password));
    if (failing.length) { setError('Password does not meet all requirements'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/signup/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, display_name: displayName, require_2fa: require2fa })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || data.details?.join(', ')); setLoading(false); return; }
      login(data.token, data.user);
      navigate('/');
    } catch { setError('Connection error'); setLoading(false); }
  };

  if (step === 'validating') {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <p className="auth-subtitle" style={{ textAlign: 'center' }}>Validating your invite...</p>
      </div></div>
    );
  }

  if (step === 'invalid') {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <div className="auth-error">{error || 'Invalid or expired invite link.'}</div>
        <p className="auth-subtitle" style={{ textAlign: 'center' }}>Please contact your admin for a new invite.</p>
      </div></div>
    );
  }

  if (step === 'send-code') {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <h2>Create Your Account</h2>
        <p className="auth-subtitle">We'll send a verification code to <strong style={{ color: '#e2e8f0' }}>{email}</strong></p>
        {error && <div className="auth-error">{error}</div>}
        <button className="auth-btn" onClick={sendCode} disabled={loading}>{loading ? 'Sending...' : 'Send Verification Code'}</button>
      </div></div>
    );
  }

  if (step === 'verify-code') {
    return (
      <div className="auth-page"><div className="auth-card">
        <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
        <h2>Enter Verification Code</h2>
        <p className="auth-subtitle">Check your email for the 6-digit code we sent to <strong style={{ color: '#e2e8f0' }}>{email}</strong></p>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={verifyCode}>
          <div className="auth-field">
            <input type="text" value={code} onChange={e => setCode(e.target.value)} placeholder="Enter 6-digit code" maxLength={6} autoFocus style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8, fontFamily: 'monospace' }} />
          </div>
          <button className="auth-btn" type="submit" disabled={loading || code.length < 6}>{loading ? 'Verifying...' : 'Verify Code'}</button>
        </form>
        <div className="auth-footer">
          <span className="auth-link" onClick={sendCode}>Resend code</span>
        </div>
      </div></div>
    );
  }

  return (
    <div className="auth-page"><div className="auth-card">
      <div className="auth-brand"><h1>Retail Alpha</h1><p>AI-Powered Alpha for Retail Investors</p></div>
      <h2>Set Up Your Account</h2>
      <p className="auth-subtitle">Create a secure password for <strong style={{ color: '#e2e8f0' }}>{email}</strong></p>
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={completeSignup}>
        <div className="auth-field">
          <label>Display Name</label>
          <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" />
        </div>
        <div className="auth-field">
          <label>Password</label>
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a strong password" required />
          <ul className="auth-pw-reqs">
            {PW_RULES.map((r, i) => (
              <li key={i} className={r.test(password) ? 'met' : 'unmet'}>
                {r.test(password) ? '✓' : '○'} {r.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="auth-field">
          <label>Confirm Password</label>
          <PasswordInput value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm your password" required />
        </div>
        <label className="auth-toggle-2fa">
          <input type="checkbox" checked={require2fa} onChange={e => setRequire2fa(e.target.checked)} />
          <span>Require email code on every login (2FA)</span>
        </label>
        <button className="auth-btn" type="submit" disabled={loading}>{loading ? 'Creating account...' : 'Create Account'}</button>
      </form>
    </div></div>
  );
}
